import React, { useState, useMemo, useEffect } from 'react';
import { useEmployers, useEmployerDetails, useVerifyEmployer, useRejectEmployer, useVerifyDocument } from '../../hooks/useAdmin';
import { debounce } from '../../utils/helpers';
import TableHeader from '../../components/common/TableHeader';
import UserDetailModal from '../../components/admin/UserDetailModal';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import RejectDialog from '../../components/common/RejectDialog';
import './AdminEmployers.css';

const AdminEmployers = () => {
  const [filters, setFilters] = useState({
    verification_status: 'all',
    search: ''
  });
  
  const [searchInput, setSearchInput] = useState('');
  
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: 'asc'
  });
  
  const [selectedUserId, setSelectedUserId] = useState(null);
  
  const { data: employers, isLoading } = useEmployers(filters);
  const { data: selectedUserDetails, isLoading: isLoadingDetails } = useEmployerDetails(selectedUserId);
  const verifyDocumentMutation = useVerifyDocument();

  // Debounced search handler - use useMemo to persist the debounced function
  const debouncedSearch = useMemo(
    () => debounce((searchValue) => {
      setFilters(prev => ({ ...prev, search: searchValue }));
    }, 300),
    []
  );

  // Update search input and trigger debounced search
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchInput(value);
    debouncedSearch(value);
  };

  // Sync searchInput with filters.search on mount or when filters.search changes externally
  useEffect(() => {
    if (filters.search !== searchInput) {
      setSearchInput(filters.search);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.search]);
  const verifyMutation = useVerifyEmployer();
  const rejectMutation = useRejectEmployer();

  const handleSort = (key, direction) => {
    setSortConfig({ key, direction });
  };

  const sortedEmployers = useMemo(() => {
    if (!employers || !sortConfig.key) return employers;
    
    return [...employers].sort((a, b) => {
      let aVal, bVal;
      
      switch (sortConfig.key) {
        case 'name':
          aVal = `${a.first_name} ${a.last_name}`.toLowerCase();
          bVal = `${b.first_name} ${b.last_name}`.toLowerCase();
          break;
        case 'email':
          aVal = a.email?.toLowerCase() || '';
          bVal = b.email?.toLowerCase() || '';
          break;
        case 'registration':
          aVal = a.registered_number?.toLowerCase() || '';
          bVal = b.registered_number?.toLowerCase() || '';
          break;
        case 'status':
          aVal = a.is_verified_by_admin ? 1 : 0;
          bVal = b.is_verified_by_admin ? 1 : 0;
          break;
        case 'joined':
          aVal = a.date_joined ? new Date(a.date_joined).getTime() : 0;
          bVal = b.date_joined ? new Date(b.date_joined).getTime() : 0;
          break;
        default:
          return 0;
      }
      
      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [employers, sortConfig]);

  const [showConfirmVerify, setShowConfirmVerify] = useState(null);
  const [showConfirmReject, setShowConfirmReject] = useState(null);
  const [rejectComment, setRejectComment] = useState('');

  const handleVerify = (id) => {
    setShowConfirmVerify(id);
  };

  const handleConfirmVerify = () => {
    if (showConfirmVerify) {
      verifyMutation.mutate({ id: showConfirmVerify, comment: '' });
      setShowConfirmVerify(null);
    }
  };

  const handleReject = (id, comment) => {
    rejectMutation.mutate({ id, comment });
  };

  const handleRejectClick = (id) => {
    setShowConfirmReject(id);
    setRejectComment('');
  };

  const handleConfirmReject = () => {
    if (showConfirmReject && rejectComment.trim()) {
      handleReject(showConfirmReject, rejectComment);
      setShowConfirmReject(null);
      setRejectComment('');
    }
  };

  const handleViewDetails = (id) => {
    setSelectedUserId(id);
  };

  const handleCloseModal = () => {
    setSelectedUserId(null);
  };

  const handleVerifyFromModal = (id) => {
    handleVerify(id);
    handleCloseModal();
  };

  const handleRejectFromModal = (id, comment) => {
    handleReject(id, comment);
    handleCloseModal();
  };

  const handleVerifyDocument = (documentId, isVerified, userId) => {
    verifyDocumentMutation.mutate({ documentId, isVerified, userId });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-GB');
  };

  if (isLoading) {
    return (
      <div className="admin-employers__loading">
        <div className="admin-employers__spinner"></div>
        <p>Loading employers...</p>
      </div>
    );
  }

  return (
    <div className="admin-employers">
      <div className="admin-employers__header">
        <h1 className="admin-employers__title">Employer Management</h1>
        <div className="admin-employers__filters">
          <select
            value={filters.verification_status}
            onChange={(e) => setFilters({ ...filters, verification_status: e.target.value })}
            className="admin-employers__filter"
          >
            <option value="all">All Employers</option>
            <option value="pending">Pending Verification</option>
            <option value="verified">Verified</option>
          </select>
          <input
            type="text"
            placeholder="Search employers..."
            value={searchInput}
            onChange={handleSearchChange}
            className="admin-employers__search"
          />
        </div>
      </div>

      {/* Desktop Table View */}
      <div className="admin-employers__table-container admin-employers__table-container--desktop">
        <table className="admin-employers__table">
          <thead>
            <tr>
              <TableHeader 
                sortKey="name" 
                currentSortKey={sortConfig.key} 
                sortDirection={sortConfig.direction}
                onSort={handleSort}
              >
                Name
              </TableHeader>
              <TableHeader 
                sortKey="email" 
                currentSortKey={sortConfig.key} 
                sortDirection={sortConfig.direction}
                onSort={handleSort}
              >
                Email
              </TableHeader>
              <TableHeader 
                sortKey="registration" 
                currentSortKey={sortConfig.key} 
                sortDirection={sortConfig.direction}
                onSort={handleSort}
              >
                Registration Number
              </TableHeader>
              <TableHeader 
                sortKey="status" 
                currentSortKey={sortConfig.key} 
                sortDirection={sortConfig.direction}
                onSort={handleSort}
              >
                Status
              </TableHeader>
              <TableHeader 
                sortKey="joined" 
                currentSortKey={sortConfig.key} 
                sortDirection={sortConfig.direction}
                onSort={handleSort}
              >
                Joined
              </TableHeader>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedEmployers && sortedEmployers.length > 0 ? (
              sortedEmployers.map((employer) => (
                <tr key={employer.id}>
                  <td>{employer.first_name} {employer.last_name}</td>
                  <td>{employer.email}</td>
                  <td>{employer.registered_number || 'N/A'}</td>
                  <td>
                    <span className={`admin-employers__status admin-employers__status--${employer.is_verified_by_admin ? 'verified' : 'pending'}`}>
                      {employer.is_verified_by_admin ? 'Verified' : 'Pending'}
                    </span>
                  </td>
                  <td>{formatDate(employer.date_joined)}</td>
                  <td>
                    <div className="admin-employers__actions">
                      <button
                        onClick={() => handleViewDetails(employer.id)}
                        className="admin-employers__btn admin-employers__btn--view"
                      >
                        View Details
                      </button>
                      {!employer.is_verified_by_admin && (
                        <>
                          <button
                            onClick={() => handleVerify(employer.id)}
                            className="admin-employers__btn admin-employers__btn--verify"
                            disabled={verifyMutation.isPending}
                          >
                            Verify
                          </button>
                          <button
                            onClick={() => handleRejectClick(employer.id)}
                            className="admin-employers__btn admin-employers__btn--reject"
                            disabled={rejectMutation.isPending}
                          >
                            Reject
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="6" className="admin-employers__empty">No employers found</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile Card View */}
      <div className="admin-employers__card-container admin-employers__card-container--mobile">
        {sortedEmployers && sortedEmployers.length > 0 ? (
          sortedEmployers.map((employer) => (
            <div key={employer.id} className="admin-employers__card">
              <div className="admin-employers__card-header">
                <div>
                  <h3 className="admin-employers__card-name">
                    {employer.first_name} {employer.last_name}
                  </h3>
                  <p className="admin-employers__card-email">{employer.email}</p>
                </div>
                <span className={`admin-employers__status admin-employers__status--${employer.is_verified_by_admin ? 'verified' : 'pending'}`}>
                  {employer.is_verified_by_admin ? 'Verified' : 'Pending'}
                </span>
              </div>
              <div className="admin-employers__card-body">
                <div className="admin-employers__card-row">
                  <span className="admin-employers__card-label">Registration Number:</span>
                  <span className="admin-employers__card-value">{employer.registered_number || 'N/A'}</span>
                </div>
                <div className="admin-employers__card-row">
                  <span className="admin-employers__card-label">Joined:</span>
                  <span className="admin-employers__card-value">{formatDate(employer.date_joined)}</span>
                </div>
              </div>
              <div className="admin-employers__card-actions">
                <button
                  onClick={() => handleViewDetails(employer.id)}
                  className="admin-employers__btn admin-employers__btn--view admin-employers__btn--mobile"
                >
                  View Details
                </button>
                {!employer.is_verified_by_admin && (
                  <>
                    <button
                      onClick={() => handleVerify(employer.id)}
                      className="admin-employers__btn admin-employers__btn--verify admin-employers__btn--mobile"
                      disabled={verifyMutation.isPending}
                    >
                      Verify
                    </button>
                    <button
                      onClick={() => handleRejectClick(employer.id)}
                      className="admin-employers__btn admin-employers__btn--reject admin-employers__btn--mobile"
                      disabled={rejectMutation.isPending}
                    >
                      Reject
                    </button>
                  </>
                )}
              </div>
            </div>
          ))
        ) : (
          <div className="admin-employers__empty admin-employers__empty--mobile">No employers found</div>
        )}
      </div>

      {selectedUserId && (
        <UserDetailModal
          user={selectedUserDetails}
          documents={selectedUserDetails?.document_status?.uploaded_documents || []}
          userType="employer"
          isLoading={isLoadingDetails}
          onClose={handleCloseModal}
          onVerify={handleVerifyFromModal}
          onReject={handleRejectFromModal}
          onVerifyDocument={handleVerifyDocument}
          userId={selectedUserId}
        />
      )}

      {/* Confirm Verify Dialog */}
      <ConfirmDialog
        isOpen={!!showConfirmVerify}
        title="Verify Employer"
        message="Are you sure you want to verify this employer? This action cannot be undone."
        confirmText="Verify"
        cancelText="Cancel"
        type="success"
        onConfirm={handleConfirmVerify}
        onCancel={() => setShowConfirmVerify(null)}
      />

      {/* Reject Dialog */}
      <RejectDialog
        isOpen={!!showConfirmReject}
        title="Reject Employer"
        message="Please provide a reason for rejection:"
        confirmText="Reject"
        cancelText="Cancel"
        value={rejectComment}
        onChange={setRejectComment}
        onConfirm={handleConfirmReject}
        onCancel={() => {
          setShowConfirmReject(null);
          setRejectComment('');
        }}
      />
    </div>
  );
};

export default AdminEmployers;

